import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProviderTreatmentStatus } from '@/services/types/treatment-types';

import { ProviderFormData } from './types';

interface TreatmentDetailsTabProps {
  register: UseFormRegister<ProviderFormData>;
  setValue: UseFormSetValue<ProviderFormData>;
  watch: UseFormWatch<ProviderFormData>;
}

export const TreatmentDetailsTab = ({
  register,
  setValue,
  watch,
}: TreatmentDetailsTabProps) => {
  const selectValue = watch('treatment_status');

  const handleTreatmentStatusChange = (value: ProviderTreatmentStatus) => {
    // Convert the select value back to the proper enum value
    if (value === 'UNKNOWN') {
      setValue('treatment_status', ProviderTreatmentStatus.UNKNOWN);
    } else {
      setValue('treatment_status', value);
    }
  };

  return (
    <div className="space-y-6">
      {/* Treatment Status */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-900">
          Current Treatment Status
        </Label>
        <Select value={selectValue} onValueChange={handleTreatmentStatusChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select treatment status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ProviderTreatmentStatus.TREATING}>
              Still Treating Patient
            </SelectItem>
            <SelectItem value={ProviderTreatmentStatus.DEVIATING}>
              Deviating from Treatment
            </SelectItem>
            <SelectItem value={ProviderTreatmentStatus.COMPLETED}>
              No Longer Treating Patient
            </SelectItem>
            <SelectItem value={ProviderTreatmentStatus.UNKNOWN}>
              Unknown/Not Specified
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">
          Whether this provider is currently treating the patient
        </p>
      </div>

      {/* Date of Records Request */}
      <div className="space-y-2">
        <Label
          htmlFor="date_of_records_request"
          className="text-sm font-medium text-gray-900"
        >
          Date of Records Request
        </Label>
        <Input
          id="date_of_records_request"
          type="date"
          {...register('date_of_records_request')}
          className="w-full"
        />
        <p className="text-xs text-gray-500">
          Date when medical records were requested from this provider
        </p>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Treatment Details Information
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  The treatment status helps track which providers are actively
                  involved in the patient&apos;s care
                </li>
                <li>
                  Record the date when you requested medical records to track
                  follow-up timing
                </li>
                <li>
                  This information can be updated later as the case progresses
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
